import styles from "./distance-chart.module.scss";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppContext } from "helpers/context";
import { MapContext } from "helpers/map";
import { getCumulativeDistanceData } from "helpers/distanceAxis";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import * as turf from "@turf/turf";
import Chart from "components/chart/chart";
import NoDataText from "../nodata-text";
import lineHighlightMarker from "assets/line_highlight_marker.svg";

// 提取点的经纬度坐标
function getLngLatFromPoint(point: any): [number, number] | null {
  const lng = Number(point?.longitude ?? point?.lng);
  const lat = Number(point?.latitude ?? point?.lat);

  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return null;
  }

  return [lng, lat];
}

function formatCoordinate(value: number) {
  return value.toFixed(6);
}

function createMarkerContent(lng: number, lat: number, lonLatLabel: string) {
  const markerContainer = document.createElement("div");
  markerContainer.style.cssText = `
    display: none;
    position: relative;
    width: 160px;
    height: 84px;
    pointer-events: none;
  `;

  const bubble = document.createElement("div");
  bubble.style.cssText = `
    position: absolute;
    top: 8px;
    left: 50%;
    transform: translateX(-50%);
    min-width: 160px;
    max-width: 200px;
    padding: 6px 10px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.75);
    color: #fff;
    font-size: 11px;
    line-height: 1.4;
    text-align: center;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
    white-space: nowrap;
  `;
  bubble.textContent = `${lonLatLabel}: ${formatCoordinate(lng)}, ${formatCoordinate(lat)}`;

  const icon = document.createElement("div");
  icon.style.cssText = `
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: 48px;
    height: 48px;
    background-image: url(${lineHighlightMarker});
    background-size: 100% 100%;
    background-repeat: no-repeat;
    background-position: center center;
  `;

  markerContainer.appendChild(bubble);
  markerContainer.appendChild(icon);

  return markerContainer;
}

interface Props {
  noData: any;
  chartData: any;
  distanceUnit?: "m" | "km";
  distanceMax?: number;
}

function DistanceChart({
  noData,
  chartData,
  distanceUnit = "km",
  distanceMax,
}: Readonly<Props>) {
  const [t] = useTranslation();
  const [context] = useContext(AppContext);
  const { AMap, map } = useContext(MapContext);
  const [chartOption, setChartOption] = useState<any>(undefined);
  const chartRef = useRef<any>(null);
  const circleMarkersRef = useRef<any[]>([]);
  const linePointsRef = useRef<any[]>([]);
  const lineFeatureRef = useRef<[number, number][]>([]);
  const lineStringFeatureRef = useRef<any>(null);
  const activeIndexRef = useRef<number | null>(null);
  const activeSeriesIndexRef = useRef<number | null>(null);
  const visibleSeriesRef = useRef<number[]>([]);

  const getMarkerByDataIndex = useCallback((dataIndex: number | null) => {
    if (dataIndex === null) {
      return undefined;
    }

    return circleMarkersRef.current.find(
      (marker: any) => marker?._dataIndex === dataIndex
    );
  }, []);

  const clearMarkers = useCallback(() => {
    if (!map || !circleMarkersRef.current.length) {
      circleMarkersRef.current = [];
      return;
    }

    map.remove(circleMarkersRef.current);
    circleMarkersRef.current = [];
  }, [map]);

  const clearHighlight = useCallback(() => {
    const currentIndex = activeIndexRef.current;
    if (currentIndex === null) {
      return;
    }

    const marker = getMarkerByDataIndex(currentIndex);
    if (marker && marker._markerContent) {
      marker._markerContent.style.display = "none";
    }

    if (chartRef.current) {
      const seriesIndexToDownplay =
        activeSeriesIndexRef.current ?? visibleSeriesRef.current[0];
      if (typeof seriesIndexToDownplay === "number") {
        chartRef.current.dispatchAction({
          type: "downplay",
          seriesIndex: seriesIndexToDownplay,
          dataIndex: currentIndex
        });
      }
      chartRef.current.dispatchAction({ type: "hideTip" });
    }

    activeIndexRef.current = null;
    activeSeriesIndexRef.current = null;
  }, [getMarkerByDataIndex]);

  const highlightByIndex = useCallback((index: number, params?: any) => {
    const marker = getMarkerByDataIndex(index);
    if (!marker) {
      return;
    }

    if (activeIndexRef.current !== null && activeIndexRef.current !== index) {
      const prevMarker = getMarkerByDataIndex(activeIndexRef.current);
      if (prevMarker && prevMarker._markerContent) {
        prevMarker._markerContent.style.display = "none";
      }
      if (chartRef.current) {
        const seriesIndexToDownplay =
          activeSeriesIndexRef.current ??
          (params?.seriesIndex !== undefined
            ? params.seriesIndex
            : visibleSeriesRef.current[0]);
        if (typeof seriesIndexToDownplay === "number") {
          chartRef.current.dispatchAction({
            type: "downplay",
            seriesIndex: seriesIndexToDownplay,
            dataIndex: activeIndexRef.current
          });
        }
      }
    }

    if (marker._markerContent) {
      marker._markerContent.style.display = "block";
    }
    activeIndexRef.current = index;

    if (chartRef.current) {
      // 如果来自图表事件，使用指定的 seriesIndex；否则只高亮第一条可见系列
      const seriesIndexToHighlight =
        params?.seriesIndex !== undefined
          ? params.seriesIndex
          : visibleSeriesRef.current[0];

      // 显示高亮系列的 Tip
      if (typeof seriesIndexToHighlight === "number") {
        chartRef.current.dispatchAction({
          type: "highlight",
          seriesIndex: seriesIndexToHighlight,
          dataIndex: index
        });
        chartRef.current.dispatchAction({
          type: "showTip",
          seriesIndex: seriesIndexToHighlight,
          dataIndex: index
        });
        activeSeriesIndexRef.current = seriesIndexToHighlight;
      } else {
        activeSeriesIndexRef.current = null;
      }
    }
  }, [getMarkerByDataIndex]);

  const getNearestPointIndex = useCallback(
    (lnglat: any) => {
      if (!AMap || !linePointsRef.current.length) {
        return -1;
      }

      let minDistance = Number.MAX_VALUE;
      let nearestIndex = -1;

      linePointsRef.current.forEach((feature, index) => {
        const [lng, lat] = feature.geometry.coordinates;
        const distance = AMap.GeometryUtil.distance([lng, lat], lnglat);
        if (distance < minDistance) {
          minDistance = distance;
          nearestIndex = feature?.properties?.dataIndex ?? index;
        }
      });

      return nearestIndex;
    },
    [AMap]
  );

  const isCursorNearLine = useCallback((lnglat: any) => {
    if (!map || !AMap?.GeometryUtil || !lineFeatureRef.current.length) {
      return false;
    }

    const lineFeature = lineStringFeatureRef.current;
    if (!lineFeature) {
      return false;
    }

    const longitude = Number(lnglat?.lng);
    const latitude = Number(lnglat?.lat);
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
      return false;
    }

    const resolution = Number(map.getResolution?.());
    if (!Number.isFinite(resolution) || resolution <= 0) {
      return false;
    }
    const metersPerPixel = resolution;
    const proximityInMeters = metersPerPixel * 4;
    const cursorPointFeature = turf.point([longitude, latitude]);
    const distanceToLineInMeters = turf.pointToLineDistance(
      cursorPointFeature,
      lineFeature,
      { units: "meters" }
    );

    return distanceToLineInMeters <= proximityInMeters;
  }, [AMap, map]);

  const updateMarkersVisibility = useCallback((nearestIndex: number) => {
    circleMarkersRef.current.forEach((marker: any) => {
      if (marker?._dataIndex === nearestIndex) {
        marker._markerContent.style.display = "block";
      } else {
        marker._markerContent.style.display = "none";
      }
    });
  }, []);

  useEffect(() => {
    const shapeObj =
      (context.currentShape as any)?.aMapRef || (context.currentShape as any);
    if (!AMap || !map || !shapeObj?.getPath) {
      clearMarkers();
      setChartOption(undefined);
      return;
    }

    const path = shapeObj.getPath?.() || [];
    if (path.length < 2) {
      clearMarkers();
      setChartOption(undefined);
      return;
    }

    const pointArray = path.map((item: any) => [item.lng, item.lat] as [number, number]);
    lineFeatureRef.current = pointArray;
    lineStringFeatureRef.current = turf.lineString(pointArray);

    // 使用 chartData 中的实际数据点来生成地图标记，确保与图表数据点一致
    let points: any[] = [];
    if (chartData && chartData.length > 0 && chartData[0]?.data) {
      // 基于 chartData[0].data 的点来生成地图上的标记
      points = chartData[0].data.map((item: any, index: number) => {
        const coords = getLngLatFromPoint(item);
        if (!coords) {
          return null;
        }
        return {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: coords
          },
          properties: {
            index,
            dataIndex: index,
            distanceAlongLine: 0
          }
        };
      }).filter((p: any) => p !== null);
    }
    linePointsRef.current = points;
    clearMarkers();

    const lonLatLabel = t("drawShape.lon/lat");

    const markers = points.map((pointFeature: any) => {
      const [lng, lat] = pointFeature.geometry.coordinates;
      const markerContent = createMarkerContent(lng, lat, lonLatLabel);

      const marker = new AMap.Marker({
        position: [
          lng,
          lat
        ],
        content: markerContent,
        offset: new AMap.Pixel(-80, -60),
        size: new AMap.Size(160, 84),
        anchor: 'top-left',
        bubble: true,
        zIndex: 20,
        clickable: false
      });

      marker._markerContent = markerContent;
      marker._dataIndex = pointFeature?.properties?.dataIndex;
      marker._hiddenStyle = { display: "none" };
      marker._highlightStyle = { display: "block" };

      return marker;
    });

    map.add(markers);
    circleMarkersRef.current = markers;

    const xAxisUnit = distanceUnit;
    const xAxisData = getCumulativeDistanceData(
      chartData?.[0]?.data || [],
      AMap,
      xAxisUnit
    );

    // 动态计算图例高度和网格底部间距
    const calculateLegendDimensions = (dataLength: number) => {
      const itemHeight = 12;
      const itemGap = 2;
      const maxItemsPerView = 9;
      const padding = 10;

      const actualItems = Math.min(dataLength, maxItemsPerView);
      const shouldAddPadding = dataLength >= maxItemsPerView;
      const legendHeight = Math.max(24, actualItems * (itemHeight + itemGap) + (shouldAddPadding ? padding : 0));
      const gridBottom = legendHeight + 50;

      return { legendHeight, gridBottom };
    };

    const { legendHeight, gridBottom } = calculateLegendDimensions(chartData?.length || 0);

    let options = {
      title: {
        text: `${t("drawShape.distance")}(${xAxisUnit === "km" ? t("drawShape.km") : t("drawShape.m")})`,
        bottom: legendHeight + 5,
        left: "center",
        textStyle: {
          fontSize: 12,
          color: "#666",
          fontWeight: "normal"
        }
      },
      grid: {
        left: 80,
        right: 20,
        top: 40,
        bottom: gridBottom
      },
      legend: {
        bottom: 0,
        left: "center",
        type: "scroll",
        orient: "vertical",
        icon: "rect",
        padding: 0,
        itemGap: 2,
        itemWidth: 16,
        itemHeight: 2,
        legendHoverLink: false,
        itemStyle: {
          opacity: 1,
          borderWidth: 0
        },
        textStyle: {
          fontWeight: "bold",
          fontSize: 11
        },
        height: legendHeight
      },
      graphic: {
        elements: [
          {
            type: "text",
            left: 30,
            bottom: gridBottom,
            style: {
              text: t("drawShape.intensityOrRiskScore"),
              fontSize: 12,
              color: "rgb(102, 102, 102)"
            },
            rotation: Math.PI / 2
          }
        ]
      },
      tooltip: {
        confine: true,
        backgroundColor: "rgba(0,0,0,0.6)",
        borderColor: "rgba(0,0,0,0.6)",
        trigger: "item",
        formatter: (params: any) => {
          const item = params?.[0] || params;
          if (!item) {
            return "";
          }
          let currentLine = chartData[params.seriesIndex];
          const idx = item.dataIndex;
          let currentPoint = currentLine?.data?.[idx] || {};
          const distance = xAxisData?.[idx] ?? 0;
          let distanceText = "";
          if (xAxisUnit === "m") {
            distanceText = t("map.length(m)", { data: parseFloat((Number(distance) || 0).toFixed(2)) });
          } else {
            distanceText = t("map.length(km)", { data: parseFloat((Number(distance) || 0).toFixed(2)) });
          }

          return `<div>
                    <p style="color:#fff;font-size:14px;">
                      ${t("layerId." + (currentPoint.layerId ?? ""))}
                    </p>
                    <p style="color:#fff;font-size:12px;margin:4px 0;display:flex;justify-content:space-between;">
                      <span>${t("legend." + currentPoint.valueLabel)}</span>
                    </p>
                    <p style="color:#fff;font-size:12px;margin:4px 0;display:flex;justify-content:space-between;">
                      <span style="margin-right: 10px">${currentPoint.layerId?.includes("Fathom") ? t("legend.Category Score/10") : t("single.Intensity")}:</span>
                      <span>${currentPoint.intensity}</span>
                    </p>
                    <p style="color:#fff;font-size:12px;margin:4px 0;display:flex;justify-content:space-between;">
                      <span style="margin-right: 10px">${t("drawShape.distance")}:</span>
                      <span>${distanceText}</span>
                    </p>
                </div>`;
        }
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: xAxisData,
        axisLine: {
          show: true,
          lineStyle: {
            color: "#ccd6eb",
          }
        },
        axisLabel: {
          color: "#666",
          formatter: (value: any) => {
            const numberValue = Number(value);
            if (!Number.isFinite(numberValue)) {
              return value;
            }
            return parseFloat(numberValue.toFixed(2));
          }
        },
        splitLine: {
          show: false
        },
        minInterval: 0.5,
      },
      yAxis: {
        type: "value",
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        }
      },
      hoverLayerThreshold: Infinity,
      // animation: false,
      series: chartData.map((item: any) => ({
        ...item,
        name: item.name ? t(`layerId.${item.name}`) : item.name
      }))
    };

    setChartOption(options);

    // 初始化所有系列为可见状态
    visibleSeriesRef.current = chartData.map((_: any, index: number) => index);

    return () => {
      clearHighlight();
      clearMarkers();
      linePointsRef.current = [];
      lineFeatureRef.current = [];
      lineStringFeatureRef.current = null;
    };
  }, [
    AMap,
    map,
    context.currentShape,
    t,
    clearHighlight,
    clearMarkers,
    chartData,
    distanceUnit,
    distanceMax
  ]);

  useEffect(() => {
    const shapeObj =
      (context.currentShape as any)?.aMapRef || (context.currentShape as any);
    if (!map?.on || !map?.off || !shapeObj?.on || !shapeObj?.off || !AMap) {
      return;
    }

    const handleMapMouseMove = (e: any) => {

      if (!isCursorNearLine(e.lnglat)) {
        clearHighlight();
        return;
      }

      const nearestIndex = getNearestPointIndex(e.lnglat);
      if (nearestIndex >= 0) {
        updateMarkersVisibility(nearestIndex);
        highlightByIndex(nearestIndex);
      } else {
        clearHighlight();
      }
    };

    const handleLineMouseOut = () => {
      clearHighlight();
    };

    map.on("mousemove", handleMapMouseMove);
    shapeObj.on("mouseout", handleLineMouseOut);

    return () => {
      map.off("mousemove", handleMapMouseMove);
      shapeObj.off("mouseout", handleLineMouseOut);
    };
  }, [
    context.currentShape,
    AMap,
    map,
    getNearestPointIndex,
    isCursorNearLine,
    highlightByIndex,
    clearHighlight,
    updateMarkersVisibility
  ]);

  const handleChartReady = useCallback(
    (chart: any | null) => {
      chartRef.current = chart;
      if (!chart) {
        return;
      }

      chart.off("mouseover");
      chart.off("mouseout");
      chart.off("globalout");
      chart.off("legendselectchanged");

      chart.on("mouseover", (params: any) => {
        if (
          params.seriesType === "line" &&
          typeof params.dataIndex === "number"
        ) {
          highlightByIndex(params.dataIndex, params);
        }
      });

      chart.on("mouseout", (params: any) => {
        if (params.seriesType === "line") {
          clearHighlight();
        }
      });

      chart.on("globalout", () => {
        clearHighlight();
      });

      // 监听图例选择变化事件，更新可见系列列表
      chart.on("legendselectchanged", (params: any) => {
        const option = chart.getOption();
        const visibleSeries: number[] = [];
        option.series.forEach((serie: any, index: number) => {
          const name = serie.name;
          // 检查该系列是否在 legend.selected 中被选中
          if (params.selected[name] !== false) {
            visibleSeries.push(index);
          }
        });
        visibleSeriesRef.current = visibleSeries.length > 0 ? visibleSeries : [];
        activeSeriesIndexRef.current = null;
      });
    },
    [clearHighlight, highlightByIndex]
  );

  return (
    <div className={styles.distanceChart}>
      <div className={styles.title}>
        <span>{t("drawShape.risk")}</span>
        <FormControl sx={{ width: 100 }} size="small">
          <InputLabel size="small">{t("drawShape.units")}</InputLabel>
          <Select
            value="km/m"
            label={t("drawShape.units")}
            disabled
            size="small"
            sx={{
              fontSize: "13px"
            }}
          >
            <MenuItem value="km/m">{t("drawShape.km/m")}</MenuItem>
          </Select>
        </FormControl>
      </div>
      <div className={styles.chart}>
        {chartOption && (
          <Chart option={chartOption} onChartReady={handleChartReady} />
        )}
      </div>
      {noData.length > 0 && <NoDataText noData={noData} />}
    </div>
  );
}

export default DistanceChart;
